import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useCustomScrollbar } from "../hooks/useCustomScrollbar";
import "./LibraryCharacters.css";

export default function LibraryScrollPane({
  className = "",
  scrollerClassName = "",
  scrollRef,
  restoreKey = "",
  restoreTop = 0,
  contentKey = "",
  onScrollPosition,
  children,
}) {
  const localRef = useRef(null);
  const restoredKeyRef = useRef("");
  const restoringRef = useRef(false);
  const [fades, setFades] = useState({ top: false, bottom: false });
  const {
    trackRef,
    scrollbar,
    updateScrollbar,
    handleThumbPointerDown,
    handleTrackPointerDown,
  } = useCustomScrollbar(localRef, { minThumbHeight: 28 });

  const assignRef = (node) => {
    localRef.current = node;
    if (scrollRef) scrollRef.current = node;
  };

  const update = useCallback(() => {
    const el = localRef.current;
    if (!el) {
      setFades({ top: false, bottom: false });
      updateScrollbar();
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = el;
    const hasOverflow = scrollHeight > clientHeight + 1;
    setFades({
      top: hasOverflow && scrollTop > 0,
      bottom: hasOverflow && scrollTop + clientHeight < scrollHeight - 0.5,
    });
    updateScrollbar();
  }, [updateScrollbar]);

  useEffect(() => {
    const el = localRef.current;
    if (!el) return undefined;
    const frame = requestAnimationFrame(update);
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    if (observer) {
      observer.observe(el);
      if (el.firstElementChild) observer.observe(el.firstElementChild);
    }
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [update, contentKey]);

  useLayoutEffect(() => {
    const node = localRef.current;
    if (!node || !restoreKey || restoredKeyRef.current === restoreKey) return undefined;
    const saved = Math.max(0, Math.round(Number(restoreTop) || 0));
    let frames = 0;
    let raf = 0;
    const tick = () => {
      restoringRef.current = true;
      node.scrollTop = saved;
      const stuck = Math.abs(node.scrollTop - saved) <= 1;
      restoringRef.current = false;
      if (stuck) {
        restoredKeyRef.current = restoreKey;
        update();
        return;
      }
      frames += 1;
      if (frames < 12) raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [restoreKey, restoreTop, contentKey, update]);

  return (
    <div
      className={`library-characters__pane${
        fades.top ? " library-characters__pane--fade-top" : ""
      }${fades.bottom ? " library-characters__pane--fade-bottom" : ""} ${className}`}
    >
      <div className="library-characters__fade-host">
        <div
          ref={assignRef}
          className={`library-characters__scroller ${scrollerClassName}`}
          onScroll={() => {
            if (restoringRef.current) {
              update();
              return;
            }
            restoredKeyRef.current = restoreKey;
            update();
            onScrollPosition?.(localRef.current?.scrollTop || 0);
          }}
        >
          {children}
        </div>
      </div>
      <div
        ref={trackRef}
        className={`library-characters__scroll-bar${
          scrollbar.visible ? "" : " library-characters__scroll-bar--hidden"
        }`}
        aria-hidden="true"
        onPointerDown={handleTrackPointerDown}
      >
        {scrollbar.visible ? (
          <div
            className="library-characters__scroll-thumb"
            style={{
              height: `${scrollbar.height}px`,
              transform: `translateY(${scrollbar.top}px)`,
            }}
            onPointerDown={handleThumbPointerDown}
          />
        ) : null}
      </div>
    </div>
  );
}
